import { createBlock } from '../../../utils/blockUtils';
import {
  getCaretSplit,
  isCaretAtStart,
  isCaretAtEnd,
  isCaretOnFirstLine,
  isCaretOnLastLine,
  stripHtml,
} from '../../../utils/caretUtils';
import { consumeListStartBackspace, inheritIndent, shouldHandleLineStartBackspace } from '../../../utils/indentUtils';
import { tryDeleteAdjacentAtomic } from '../../../utils/atomicCaret';

const commitContent = (el, contentRef, setContent, onUpdate, blockId) => {
  const value = el.innerHTML;
  contentRef.current = value;
  setContent(value);
  onUpdate(blockId, { content: value });
};

/**
 * Notion-grade paragraph keys after slash/variable overlays have had their say:
 * Enter splits (or escapes an empty last callout line); arrows walk to the
 * neighbouring block at visual edges; Backspace outdents then merges;
 * Delete merges or removes.
 * Returns true when the event should not fall through.
 */
export const handleTextEditingKeys = (
  e,
  {
    el,
    block,
    contentRef,
    setContent,
    onUpdate,
    onUpdateAndInsert,
    onInsertAfter,
    onDelete,
    onMergeWithPrevious,
    onMergeWithNext,
    onNavigate,
    onEscapeContainer,
    slashMenuOpen,
  }
) => {
  // Enter splits at the caret (or starts a fresh paragraph at the end).
  // On an EMPTY last line of a callout it escapes the callout instead.
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    const isEmpty = stripHtml(contentRef.current).trim() === '';
    if (isEmpty && onEscapeContainer?.(block.id)) return true;
    const split = el ? getCaretSplit(el) : null;
    if (split && stripHtml(split.after).length > 0) {
      contentRef.current = split.before;
      setContent(split.before);
      onUpdateAndInsert(
        block.id,
        { content: split.before },
        createBlock('text', split.after, inheritIndent(block)),
        { focusAtStart: true }
      );
    } else {
      onInsertAfter?.(block.id, createBlock('text', '', inheritIndent(block)));
    }
    return true;
  }

  // Arrow navigation across blocks (first/last VISUAL line only)
  if (e.key === 'ArrowUp' && el && isCaretOnFirstLine(el)) {
    if (onNavigate?.(block.id, 'up')) e.preventDefault();
    return true;
  }
  if (e.key === 'ArrowDown' && el && isCaretOnLastLine(el)) {
    if (onNavigate?.(block.id, 'down')) e.preventDefault();
    return true;
  }
  // Left/right at block edges — skip when modifiers want word/selection nav
  if (
    e.key === 'ArrowLeft' &&
    !e.shiftKey &&
    !e.altKey &&
    !e.metaKey &&
    !e.ctrlKey &&
    el &&
    isCaretAtStart(el)
  ) {
    if (onNavigate?.(block.id, 'left')) e.preventDefault();
    return true;
  }
  if (
    e.key === 'ArrowRight' &&
    !e.shiftKey &&
    !e.altKey &&
    !e.metaKey &&
    !e.ctrlKey &&
    el &&
    isCaretAtEnd(el)
  ) {
    if (onNavigate?.(block.id, 'right')) e.preventDefault();
    return true;
  }

  if (e.key === 'Backspace' && !slashMenuOpen) {
    const isEmpty = stripHtml(contentRef.current).trim() === '';
    const atLineStart = shouldHandleLineStartBackspace(e, el, {
      isEmpty,
      blockId: block.id,
      content: contentRef.current,
    });
    if (atLineStart) {
      if (
        consumeListStartBackspace(e, block, {
          content: contentRef.current,
          onUpdate,
          onMergeWithPrevious,
        })
      ) {
        return true;
      }
      if (isEmpty) {
        e.preventDefault();
        onDelete?.(block.id);
        return true;
      }
      if (onMergeWithPrevious?.(block.id, contentRef.current)) {
        e.preventDefault();
        return true;
      }
    }
  }

  // Variable/mention pills: Backspace after (Delete before) removes the atom
  if (!slashMenuOpen && tryDeleteAdjacentAtomic(e)) {
    if (el) commitContent(el, contentRef, setContent, onUpdate, block.id);
    return true;
  }

  // Delete at end: pull the next block's text up (incl. numbered/bullets)
  if (e.key === 'Delete' && el && isCaretAtEnd(el)) {
    const live = el.innerHTML;
    contentRef.current = live;
    setContent(live);
    if (onMergeWithNext?.(block.id, live)) {
      e.preventDefault();
      return true;
    }
  }

  return false;
};
